import type { Id, Timestamp } from '@/model';

export type CallKind = 'voice' | 'video' | 'unknown';
export type CallState = 'ringing' | 'active' | 'ended' | 'declined' | 'missed' | 'cancelled' | 'failed' | 'unknown';
export type SignalType = 'offer' | 'answer' | 'candidate' | 'hangup' | 'media' | 'unknown';
export interface IceServer { urls: string[]; username?: string; credential?: string }
export interface CallMetadata {
  id: Id; conversation_id: Id; caller_id: Id; callee_id: Id;
  kind: CallKind; state: CallState; created_at: Timestamp;
  answered_at: Timestamp | null; ended_at: Timestamp | null; end_reason: string | null;
}
/** Ephemeral. Never put this object in a query cache, database, or diagnostic output. */
export interface CallWire extends CallMetadata { ice_servers: IceServer[] }
export interface StartCallInput { client_id: Id; conversation_id: Id; kind: 'voice' | 'video' }
export interface SignalPayload {
  sdp?: string; candidate?: string; sdp_mid?: string | null; sdp_mline_index?: number;
  video_capable?: boolean; video_enabled?: boolean; reason?: string;
}
export interface CallSignal { call_id: Id; from: Id; type: SignalType; payload: SignalPayload }
export interface CallApi {
  start(input: StartCallInput): Promise<CallWire>;
  active(signal?: AbortSignal): Promise<CallWire[]>;
  get(id: Id, signal?: AbortSignal): Promise<CallWire>;
  accept(id: Id): Promise<CallWire>;
  decline(id: Id): Promise<CallWire>;
  cancel(id: Id): Promise<CallWire>;
  end(id: Id): Promise<CallWire>;
  signal(id: Id, type: Exclude<SignalType, 'unknown'>, payload: SignalPayload): Promise<void>;
}

export const object = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
export const validId = (id: unknown): id is Id => typeof id === 'string' && /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(id);
export const isLiveCall = (call: Pick<CallMetadata, 'state'>) => call.state === 'ringing' || call.state === 'active';
export const peerId = (call: CallMetadata, me: Id) => call.caller_id === me ? call.callee_id : call.caller_id;
export const isParty = (call: CallMetadata, me: Id) => call.caller_id === me || call.callee_id === me;
const timestamp = (v: unknown): v is string => typeof v === 'string' && Number.isFinite(Date.parse(v));

export function decodeCall(value: unknown): CallWire {
  const v = object(value);
  if (!v || !validId(v.id) || !validId(v.conversation_id) || !validId(v.caller_id) || !validId(v.callee_id) || !timestamp(v.created_at)) throw new Error('invalid_call');
  const states = ['ringing', 'active', 'ended', 'declined', 'missed', 'cancelled', 'failed'];
  const ice: IceServer[] = [];
  if (Array.isArray(v.ice_servers)) for (const raw of v.ice_servers) {
    const entry = object(raw);
    if (!entry) continue;
    const urls = typeof entry.urls === 'string' ? [entry.urls] : Array.isArray(entry.urls) ? entry.urls.filter((u): u is string => typeof u === 'string') : [];
    if (urls.length) ice.push({ urls, ...(typeof entry.username === 'string' ? { username: entry.username } : {}), ...(typeof entry.credential === 'string' ? { credential: entry.credential } : {}) });
  }
  return {
    id: v.id, conversation_id: v.conversation_id, caller_id: v.caller_id, callee_id: v.callee_id,
    kind: v.kind === undefined || v.kind === 'voice' ? 'voice' : v.kind === 'video' ? 'video' : 'unknown',
    state: typeof v.state === 'string' && states.includes(v.state) ? v.state as CallState : 'unknown',
    created_at: v.created_at, answered_at: timestamp(v.answered_at) ? v.answered_at : null,
    ended_at: timestamp(v.ended_at) ? v.ended_at : null, end_reason: typeof v.end_reason === 'string' ? v.end_reason : null,
    ice_servers: ice,
  };
}

export function metadata(call: CallMetadata): CallMetadata {
  return { id: call.id, conversation_id: call.conversation_id, caller_id: call.caller_id, callee_id: call.callee_id, kind: call.kind, state: call.state, created_at: call.created_at, answered_at: call.answered_at, ended_at: call.ended_at, end_reason: call.end_reason };
}

export function encodePayload(payload: SignalPayload): string {
  const wire = JSON.stringify(payload);
  if (new TextEncoder().encode(wire).byteLength > 16 * 1024) throw new Error('signal_too_large');
  return wire;
}

export function decodeSignal(value: unknown): CallSignal | null {
  const v = object(value);
  if (!v || !validId(v.call_id) || !validId(v.from)) return null;
  const type: SignalType = ['offer', 'answer', 'candidate', 'hangup', 'media'].includes(String(v.type)) ? v.type as SignalType : 'unknown';
  let raw = object(v.payload);
  if (typeof v.payload === 'string') {
    if (new TextEncoder().encode(v.payload).byteLength > 16 * 1024) return null;
    try { raw = object(JSON.parse(v.payload)); } catch { /* Legacy bare SDP or candidate. */ }
    if (!raw && (type === 'offer' || type === 'answer')) raw = { sdp: v.payload };
    if (!raw && type === 'candidate') raw = { candidate: v.payload };
  }
  const payload: SignalPayload = {};
  if (raw) {
    for (const key of ['sdp', 'candidate', 'reason'] as const) if (typeof raw[key] === 'string') payload[key] = raw[key];
    if (raw.sdp_mid === null || typeof raw.sdp_mid === 'string') payload.sdp_mid = raw.sdp_mid;
    if (Number.isInteger(raw.sdp_mline_index) && Number(raw.sdp_mline_index) >= 0 && Number(raw.sdp_mline_index) < 65536) payload.sdp_mline_index = Number(raw.sdp_mline_index);
    if (typeof raw.video_capable === 'boolean') payload.video_capable = raw.video_capable;
    if (typeof raw.video_enabled === 'boolean') payload.video_enabled = raw.video_enabled;
  }
  return { call_id: v.call_id, from: v.from, type, payload };
}
